/**
 * finance-core — Mục tiêu tài chính & quỹ khẩn cấp (GĐ5). Thuần hàm, có test.
 * Mọi con số "cần góp mỗi tháng / bao giờ đạt" đi qua compound.ts (lãi kép, tính cả số dư hiện có).
 */
import { requiredMonthlySavings, monthsToReachGoal } from './compound';
import { DEFAULT_INVESTMENT_RETURN_PERCENT } from './fire';

export type GoalTerm = 'short' | 'medium' | 'long';
export interface GoalLike { name?: string; targetAmount?: number; currentAmount?: number; deadline?: any; expectedReturn?: number | null }

/** Lợi nhuận mặc định theo kỳ hạn: ngắn hạn không rủi ro (0%), trung hạn thận trọng (4%), dài hạn = giả định chung của app. */
export const goalReturnDefault = (years: number | null): number =>
  years === null ? DEFAULT_INVESTMENT_RETURN_PERCENT : years <= 1 ? 0 : years <= 5 ? 4 : DEFAULT_INVESTMENT_RETURN_PERCENT;

const toDate = (v: any): Date | null => {
  if (!v) return null;
  const d = typeof v.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** unknown = có hạn nhưng chưa cho biết mức góp để so sánh */
export type GoalStatus = 'done' | 'on-track' | 'behind' | 'no-deadline' | 'overdue' | 'unknown';
export interface GoalProgress {
  target: number; current: number; remaining: number; pct: number;
  /** Số năm tới hạn (null nếu chưa đặt hạn) */
  yearsLeft: number | null;
  returnPct: number;
  /** Số tiền cần góp MỖI THÁNG để đúng hạn (0 nếu đã đủ; null nếu chưa có hạn) */
  requiredMonthly: number | null;
  /** Khi góp `monthlyContribution` mỗi tháng: số tháng đến lúc đạt (Infinity nếu không bao giờ) */
  monthsAtContribution: number | null;
  projectedDate: Date | null;
  status: GoalStatus;
}

export const goalProgress = (goal: GoalLike, opts: { monthlyContribution?: number; now?: Date } = {}): GoalProgress => {
  const now = opts.now ?? new Date();
  const target = Number(goal.targetAmount) || 0, current = Number(goal.currentAmount) || 0;
  const remaining = Math.max(0, target - current);
  const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  const dl = toDate(goal.deadline);
  // Kỳ hạn theo THÁNG LỊCH (đúng 1 năm = 12,0 tháng) — tránh lệch số lẻ do năm nhuận
  const yearsLeft = dl
    ? ((dl.getFullYear() - now.getFullYear()) * 12 + (dl.getMonth() - now.getMonth()) + (dl.getDate() - now.getDate()) / 30.4375) / 12
    : null;
  const returnPct = goal.expectedReturn != null ? Number(goal.expectedReturn) : goalReturnDefault(yearsLeft);

  let requiredMonthly: number | null = null;
  if (yearsLeft !== null) {
    requiredMonthly = remaining <= 0 ? 0 : yearsLeft <= 0 ? Infinity : Math.max(0, requiredMonthlySavings(current, target, returnPct, yearsLeft));
  }
  const c = opts.monthlyContribution;
  const monthsAtContribution = c === undefined ? null : remaining <= 0 ? 0 : monthsToReachGoal(current, c, returnPct, target);
  const projectedDate = monthsAtContribution !== null && Number.isFinite(monthsAtContribution)
    ? new Date(now.getFullYear(), now.getMonth() + monthsAtContribution, 1) : null;

  let status: GoalStatus;
  if (target > 0 && current >= target) status = 'done';
  else if (!dl) status = 'no-deadline';
  else if (yearsLeft! <= 0) status = 'overdue';
  else if (monthsAtContribution === null) status = 'unknown';
  else status = monthsAtContribution <= yearsLeft! * 12 ? 'on-track' : 'behind';
  return { target, current, remaining, pct, yearsLeft, returnPct, requiredMonthly, monthsAtContribution, projectedDate, status };
};

/** Tổng góp mỗi tháng cho mọi mục tiêu có hạn so với khả năng để dành thực tế. */
export const planFeasibility = (progress: GoalProgress[], monthlySavings: number) => {
  const need = progress.reduce((s, p) => s + (p.requiredMonthly && Number.isFinite(p.requiredMonthly) ? p.requiredMonthly : 0), 0);
  const impossible = progress.filter(p => p.status !== 'done' && p.requiredMonthly === Infinity).length;
  return {
    totalRequired: need, impossible,
    level: monthlySavings <= 0 && need > 0 ? ('none' as const) : need <= monthlySavings ? ('ok' as const) : ('short' as const),
    gap: Math.max(0, need - Math.max(0, monthlySavings))
  };
};

// ───────── Quỹ khẩn cấp ─────────
export const essentialMonthlyExpense = (
  expenses: Array<{ year?: number; month?: number; category?: string; categoryName?: string; amount?: number; isRefund?: boolean; autoGenerated?: boolean }>,
  classByName: Record<string, 'need' | 'want' | undefined>,
  now: Date = new Date(),
  months = 6
): number => {
  const keys = new Set<string>();
  for (let i = 1; i <= months; i++) { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); keys.add(`${d.getFullYear()}-${d.getMonth() + 1}`); }
  const active = new Set<string>(); let sum = 0;
  for (const e of expenses) {
    const k = `${e.year}-${e.month}`;
    if (!keys.has(k)) continue;
    active.add(k);
    if (classByName[e.category || e.categoryName || ''] === 'need') sum += (e.isRefund ? -1 : 1) * (Number(e.amount) || 0);
  }
  return sum / Math.max(1, active.size);
};

export interface EmergencyStatus {
  monthsCovered: number | null; target: number; gap: number; pct: number;
  level: 'bad' | 'warn' | 'good'; monthsToFill: number | null;
}
/** Quỹ khẩn cấp so với chi tiêu THIẾT YẾU hằng tháng (chuẩn 3–6 tháng); `monthlySavings` để ước lượng khi nào đủ. */
export const emergencyStatus = (input: { fund: number; monthlyEssential: number; targetMonths: number; monthlySavings: number }): EmergencyStatus => {
  const { fund, monthlyEssential, targetMonths, monthlySavings } = input;
  const target = monthlyEssential * targetMonths;
  const gap = Math.max(0, target - fund);
  const monthsCovered = monthlyEssential > 0 ? fund / monthlyEssential : null;
  return {
    monthsCovered, target, gap, pct: target > 0 ? Math.min(100, (fund / target) * 100) : 0,
    level: monthsCovered === null ? 'warn' : monthsCovered >= Math.min(targetMonths, 6) ? 'good' : monthsCovered >= 3 ? 'warn' : 'bad',
    monthsToFill: gap <= 0 ? 0 : monthlySavings > 0 ? Math.ceil(gap / monthlySavings) : null
  };
};

/** Các chặng quỹ khẩn cấp (số tháng chi thiết yếu): đi từng chặng thay vì nhắm thẳng 6–9 tháng. */
export const EMERGENCY_STAGES = [1, 3, 6, 9] as const;
/** Chặng đã đạt (cao nhất) và chặng kế tiếp theo số tháng quỹ đang trang trải được. */
export const emergencyStage = (monthsCovered: number | null): { reached: number | null; next: number | null } => {
  const m = monthsCovered ?? 0;
  const reached = [...EMERGENCY_STAGES].reverse().find(s => m >= s) ?? null;
  const next = EMERGENCY_STAGES.find(s => m < s) ?? null;
  return { reached, next };
};

/**
 * Chia khả năng để dành hằng tháng cho các mục tiêu theo TỈ LỆ số tiền mỗi mục tiêu cần góp
 * (không mục tiêu nào được nhiều hơn mức nó cần). Mục tiêu chưa có hạn/đã đủ nhận 0.
 */
export const allocateSavings = (progress: GoalProgress[], monthlySavings: number): number[] => {
  const need = progress.map(p => (p.status === 'done' || p.requiredMonthly === null || !Number.isFinite(p.requiredMonthly) ? 0 : p.requiredMonthly));
  const total = need.reduce((a, b) => a + b, 0);
  const pool = Math.max(0, monthlySavings);
  if (total <= 0) return need.map(() => 0);
  return need.map(n => Math.min(n, (n / total) * pool));
};
